/**
 * Traffic Source Detection & Affiliate Tag Management
 *
 * Detects where users came from (Facebook, Twitter, etc.) and
 * swaps affiliate tags accordingly for better conversion tracking.
 */

const AFFILIATE_TAGS = {
  facebook: 'lda-page-20',
  twitter: 'lpd-twitter-20',
  default: 'voiceshoppi0b-20',
};

const COOKIE_NAME = 'lpd_traffic_source';
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Detect traffic source from UTM params or referrer
 */
function detectSource() {
  // Check UTM parameter first (most reliable)
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source')?.toLowerCase();

  if (utmSource) {
    if (utmSource.includes('facebook') || utmSource === 'fb') return 'facebook';
    if (utmSource.includes('twitter') || utmSource === 'tw' || utmSource === 'x') return 'twitter';
  }

  // Fall back to referrer
  const referrer = document.referrer.toLowerCase();
  if (referrer.includes('facebook.com') || referrer.includes('fb.com') || referrer.includes('m.facebook.com')) {
    return 'facebook';
  }
  if (referrer.includes('twitter.com') || referrer.includes('t.co') || referrer.includes('x.com')) {
    return 'twitter';
  }

  return null;
}

/**
 * Get cookie value
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

/**
 * Set cookie with expiry
 */
function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * Initialize traffic source tracking
 * Call this on app load or page load
 * Uses last-touch attribution - always updates to most recent source
 */
export function initTrafficSource() {
  const detectedSource = detectSource();
  if (detectedSource) {
    setCookie(COOKIE_NAME, detectedSource, COOKIE_EXPIRY_DAYS);
  }
}

/**
 * Get the current traffic source
 */
export function getTrafficSource() {
  return getCookie(COOKIE_NAME) || 'direct';
}

/**
 * Get the affiliate tag for the current traffic source
 */
export function getAffiliateTag() {
  const source = getTrafficSource();
  return AFFILIATE_TAGS[source] || AFFILIATE_TAGS.default;
}

/**
 * Transform an Amazon URL to use the correct affiliate tag
 * @param {string} url - Original Amazon URL
 * @returns {string} - URL with correct affiliate tag
 */
export function transformAffiliateUrl(url) {
  if (!url) return url;

  const newTag = getAffiliateTag();

  // Replace existing tag or append new one
  if (url.includes('tag=')) {
    return url.replace(/tag=[^&]+/, `tag=${newTag}`);
  } else {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}tag=${newTag}`;
  }
}

export { AFFILIATE_TAGS };
